import { useEffect, useRef } from 'react';
import { Card } from './ui/card';

export const StagnantRevenueChart = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    let frame = 0;
    const maxFrames = 200;

    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw grid lines
      ctx.strokeStyle = '#333333';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const y = (height * i) / 5;
        ctx.beginPath();
        ctx.moveTo(60, y);
        ctx.lineTo(width - 20, y);
        ctx.stroke();
      }

      // Draw axes
      ctx.strokeStyle = '#888888';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(60, 0);
      ctx.lineTo(60, height);
      ctx.lineTo(width - 20, height);
      ctx.stroke();

      // Y-axis labels
      ctx.fillStyle = '#888888';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'right';
      for (let i = 0; i <= 5; i++) {
        const value = `$${(50 - i * 10)}k`;
        const y = (height * i) / 5;
        ctx.fillText(value, 50, y + 4);
      }

      // X-axis label
      ctx.textAlign = 'center';
      ctx.fillText('Time →', width / 2, height + 30);

      // Calculate current progress
      const progress = Math.min(frame / maxFrames, 1);
      const points = 50;

      // Draw Paid Media Spend (Green - increasing)
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (let i = 0; i <= points * progress; i++) {
        const x = 60 + ((width - 80) * i) / points;
        const baseY = height - 50;
        const trend = -150 * (i / points); // Upward trend
        const noise = Math.sin(i * 0.3) * 5; // Small variation
        const y = baseY + trend + noise;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();

      // Draw Brand Revenue (Red - stagnant)
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (let i = 0; i <= points * progress; i++) {
        const x = 60 + ((width - 80) * i) / points;
        const baseY = height - 100;
        const drift = Math.sin(i * 0.2) * 8; // Slight fluctuation but mostly flat
        const y = baseY + drift;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();

      // Continue animation only if not complete
      if (frame < maxFrames) {
        frame++;
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <Card className="bg-[#1a1a1a] border-none p-8">
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h3 className="text-2xl font-bold text-[#F5F5F5]">The plateau problem</h3>
          <p className="text-[#888888]">Spend increases. Revenue doesn't.</p>
        </div>

        <div className="relative">
          <canvas
            ref={canvasRef}
            width={800}
            height={300}
            className="w-full"
            style={{ maxWidth: '800px', margin: '0 auto', display: 'block' }}
          />
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-8 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-[#22c55e]"></div>
            <span className="text-[#888888]">Paid Media Spend</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-[#ef4444]"></div>
            <span className="text-[#888888]">Brand Revenue</span>
          </div>
        </div>
      </div>
    </Card>
  );
};
